import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Switch, Tooltip } from '@material-ui/core';
import { colors } from '@/styles';
import { BILLABLE_ERROR, BILLABLE_HELP, BILLABLE_INVOICED_HELP } from './constants';
import { useSetShipperBillable } from './hooks';
import { isInvoiced, readSplitError } from './utils';

/**
 * "Billable:" label and switch for a split child. Changing it updates `orders.shipper_billable`; the switch
 * is disabled while the call runs, and on failure it goes back and shows the server's message. It is disabled with
 * an explanation when the order is invoiced. Reusable on a child's Order Details.
 *
 * @param {{ order: { order_id: string, shipper_billable?: boolean, invoicing_status?: string|null, shipper_invoice_id?: string|null, carrier_invoice_id?: string|null, middle_mile_carrier_invoice_id?: string|null, warehouse_carrier_invoice_id?: string|null }, disabled?: boolean, hideLabel?: boolean, onChanged?: (order: object) => void }} props
 *   `disabled` turns the switch off from outside. `hideLabel` omits the "Billable:" text (the switch keeps an aria-label) for rows that already have a label. `onChanged` runs with the updated order after a successful change.
 */
export const BillableToggle = ({ order, disabled = false, hideLabel = false, onChanged }) => {
    const { setBillable, saving } = useSetShipperBillable();
    const [value, setValue] = useState(order?.shipper_billable !== false);
    const [errorMessage, setErrorMessage] = useState('');
    const [latest, setLatest] = useState(order);

    // Follow the order when the parent hands in a newer one (for example after a refetch).
    // Keyed on the fields it reads, so a re-render with an equal order does not undo an optimistic flip.
    const { shipper_billable, invoicing_status } = order || {};
    const invoiceKey = [
        order?.shipper_invoice_id,
        order?.carrier_invoice_id,
        order?.middle_mile_carrier_invoice_id,
        order?.warehouse_carrier_invoice_id,
    ].join('|');
    useEffect(() => {
        setLatest(order);
        setValue(shipper_billable !== false);
    }, [shipper_billable, invoicing_status, invoiceKey]);

    const invoiced = isInvoiced(latest);
    const help = invoiced ? BILLABLE_INVOICED_HELP : BILLABLE_HELP;
    const id = order?.order_id;

    const handleChange = async (event) => {
        const next = event.target.checked;
        const previous = value;
        setErrorMessage('');
        setValue(next);
        const result = await setBillable(id, next);
        if (!result) return;
        if (result.error) {
            setValue(previous);
            setErrorMessage(readSplitError(result.error).message);
            return;
        }
        if (!result.order) {
            setValue(previous);
            setErrorMessage(BILLABLE_ERROR);
            return;
        }
        setLatest((current) => ({ ...current, ...result.order }));
        setValue(result.order.shipper_billable !== false);
        onChanged?.({ ...order, ...result.order });
    };

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-end;
            `}
        >
            <Tooltip title={help} placement="top" arrow>
                <label
                    data-testid={`billable-${id}`}
                    css={css`
                        display: flex;
                        align-items: center;
                        font-size: 13px;
                        font-weight: 500;
                    `}
                >
                    {!hideLabel && <span id={`billable-label-${id}`}>Billable:</span>}
                    <Switch
                        color="primary"
                        checked={value}
                        onChange={handleChange}
                        disabled={disabled || invoiced || saving}
                        inputProps={
                            hideLabel
                                ? { 'aria-label': 'Billable to shipper' }
                                : { 'aria-labelledby': `billable-label-${id}` }
                        }
                    />
                </label>
            </Tooltip>
            {errorMessage && (
                <span
                    role="alert"
                    css={css`
                        color: ${colors.reds.primary};
                        font-size: 12px;
                        max-width: 280px;
                        text-align: right;
                    `}
                >
                    {errorMessage}
                </span>
            )}
        </div>
    );
};
